import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Container,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Lock as LockIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useAppConfig } from '../contexts/AppConfigContext';

const Login = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, loading: authLoading } = useAuth();
  const { schoolName, schoolAddress, themeColor, loading: configLoading } = useAppConfig();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);
      if (result.success) {
        // Check user role and redirect accordingly
        const userRole = result.user?.role_name || localStorage.getItem('userRole');
        if (userRole === 'Parent') {
          navigate('/my-child', { replace: true });
        } else {
          setError('Only parents can access this portal');
        }
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || configLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: 'background.default',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const primaryColor = themeColor || theme.palette.primary.main;

  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  const rgb = hexToRgb(primaryColor);
  let gradientBackground = `linear-gradient(135deg, ${primaryColor} 0%, ${theme.palette.primary.dark || primaryColor} 100%)`;
  if (rgb) {
    const darkerR = Math.max(0, Math.floor(rgb.r * 0.7));
    const darkerG = Math.max(0, Math.floor(rgb.g * 0.7));
    const darkerB = Math.max(0, Math.floor(rgb.b * 0.7));
    const darkerShade = `rgb(${darkerR}, ${darkerG}, ${darkerB})`;
    gradientBackground = `linear-gradient(135deg, ${primaryColor} 0%, ${darkerShade} 100%)`;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        position: 'relative',
        background: gradientBackground,
      }}
    >
      {/* Top 25% Image Area */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '38%',
          overflow: 'hidden',
          zIndex: 0,
        }}
      >
        <Box
          component="img"
          src="/banner1.png"
          alt="School Banner"
          onError={(e) => {
            e.target.style.display = 'none';
          }}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: 0.9,
          }}
        />
        {/* Overlay for better text/image contrast if needed */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, transparent 100%)`,
          }}
        />
      </Box>

      {/* White Box - starts from 30% and extends to bottom (70% content) */}
      <Box
        sx={{
          position: 'absolute',
          top: '30%',
          left: 0,
          right: 0,
          bottom: 0,
          bgcolor: 'background.paper',
          borderTopLeftRadius: { xs: '64px', sm: '64px' },
          borderTopRightRadius: { xs: '64px', sm: '64px' },
          boxShadow: '0 -4px 30px rgba(0,0,0,0.2)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Content Container */}
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            px: 3,
            py: { xs: 2, sm: 3 },
            overflowY: 'auto',
            minHeight: 0,
          }}
        >
          <Container maxWidth="sm" sx={{ textAlign: 'center', width: '100%' }}>
            {/* School Logo */}
            <Box
              sx={{
                mb: { xs: 2, sm: 2.5 },
                display: 'flex',
                justifyContent: 'center',
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  display: 'inline-block',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    inset: -8,
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, ${primaryColor}22, ${primaryColor}11)`,
                    zIndex: -1,
                  },
                }}
              >
                <img
                  src="/school_logo.png"
                  alt="School Logo"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                  style={{
                    width: isMobile ? 100 : 120,
                    height: isMobile ? 100 : 120,
                  }}
                />
              </Box>
            </Box>

            {/* School Name */}
            <Typography
              variant="h4"
              fontWeight={700}
              sx={{
                mb: 1,
                color: 'text.primary',
                fontSize: { xs: '1.5rem', sm: '1.9rem' },
                letterSpacing: '-0.5px',
              }}
            >
              {schoolName || 'School Management'}
            </Typography>

            {/* School Address */}
            {schoolAddress && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ 
                  mb: 2, 
                  fontSize: { xs: '0.85rem', sm: '0.95rem' },
                  fontWeight: 400,
                  fontStyle: 'italic',
                }}
              >
                {schoolAddress}
              </Typography>
            )}

            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ 
                mb: { xs: 3, sm: 3.5 }, 
                fontSize: { xs: '0.95rem', sm: '1.1rem' },
                fontWeight: 500,
              }}
            >
              Parent Portal
            </Typography>

            {/* Login Form */}
            <Box component="form" onSubmit={handleSubmit} sx={{ textAlign: 'left' }}>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <TextField
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                InputProps={{
                  startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
                sx={{ mb: 2 }}
                size="small"
              />

              <TextField
                required
                fullWidth
                name="password"
                label="Password"
                type="password"
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  startAdornment: <LockIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
                sx={{ mb: 2.5 }}
                size="small"
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  py: 1.5,
                  borderRadius: 3,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
                  background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)`,
                  '&:hover': {
                    boxShadow: '0 8px 25px rgba(0,0,0,0.3)',
                    transform: 'translateY(-3px)',
                    background: `linear-gradient(135deg, ${primaryColor}dd 0%, ${primaryColor} 100%)`,
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                {loading ? 'Signing In...' : 'Sign In'}
              </Button>
            </Box>
          </Container>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            py: { xs: 1.5, sm: 2 },
            px: 3,
            textAlign: 'center',
            borderTop: '1px solid',
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            Developed by{' '}
            <strong style={{ fontWeight: 700 }}>
              <a
                href="https://ptshub.in/"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: 'inherit',
                  textDecoration: 'none',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                  e.target.style.textDecoration = 'underline';
                }}
                onMouseLeave={(e) => {
                  e.target.style.textDecoration = 'none';
                }}
              >
                PTS Hub
              </a>
            </strong>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
